import { createRoot } from 'react-dom/client';
import { matchPath } from 'react-router-dom';

import './trusted-security-policies';
import './extensions';
import 'simplebar/src/simplebar.css';
import 'react-modern-drawer/dist/index.css';
// kula's stylesheet before the app's own: both ship plain Tailwind utilities at
// equal specificity, so whichever loads last wins a tie. With kula last, a
// utility passed to one of its components (`triggerClassName="pl-4"` over the
// variant's own `px-3`) silently lost, and the fix looked like it had to be
// `!important` or a second copy of kula's classes in our Tailwind build.
import '@kula-ai/components/style.css';
import './styles/index.css';
import { createBrowserHistory } from 'history';

import { App as Core } from './app';
import './utils/yup';

// integrations
import { withLDProvider } from 'launchdarkly-react-client-sdk';
import * as Sentry from '@sentry/react';
import { SENTRY_ROUTE_PATTERNS } from '@/constants/routePatterns';
import { installDomMutationGuards } from '@/lib/domMutationGuards';
import { whenIdle } from '@/utils/whenIdle';
import { loadPosthog } from '@/lib/posthogClient';
import { AssessmentApp } from './assessment/app';
import { IntegrationApp } from 'integrations/app';
import { CandidateSurveyApp } from './candidateSurvey/app';
import { CandidateConsentApp } from './candidateConsent/app';

if (import.meta.env.VITE_SENTRY_DSN) {
  const history = createBrowserHistory();

  const EXTENSION_URL_RE =
    /^(chrome-extension|moz-extension|safari-extension|safari-web-extension):\/\//;

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_ENV,
    // `sentryVitePlugin` already uploads sourcemaps under this release name
    // (vite.config.ts), and all five deploy workflows export VITE_RELEASE — but
    // without it here the events carry no release, so Sentry has nothing to
    // match those artifacts against and stack traces stay minified. Setting it
    // also makes per-release comparison possible, which is what lets a
    // performance change be attributed to the deploy that shipped it rather
    // than to whatever else happened to land that week.
    release: import.meta.env.VITE_RELEASE,
    integrations: [
      new Sentry.Replay(),
      new Sentry.BrowserTracing({
        routingInstrumentation: Sentry.reactRouterV5Instrumentation(
          history,
          SENTRY_ROUTE_PATTERNS,
          matchPath
        ),
        shouldCreateSpanForRequest: (url) =>
          !url.includes('kula-ai-attachments-prod.s3.amazonaws.com/resumes'),
      }),
    ],

    // Unattended session replays are off: nobody watches them, and at 0.1 they
    // were the bulk of the org's replay spend. Once that quota is exhausted
    // Sentry drops EVERY replay — including the error ones below — so recording
    // sessions nobody opens was crowding out the ones worth having.
    // `beforeSend` cannot help here; it only runs for error events.
    replaysSessionSampleRate: 0,
    // Unchanged: a replay attached to a real error is the one worth keeping.
    replaysOnErrorSampleRate: 1.0,
    // Raised from 0.1 while establishing a per-route performance baseline: the
    // route table above was only covering 32 of 58 pages, so the busy routes
    // have usable data but the quieter ones are too thin to read. Expect ~3x the
    // transaction volume against the Sentry quota — drop back to 0.1 once a
    // couple of weeks of data has accumulated.
    tracesSampleRate: 0.3,
    ignoreErrors: [
      /^ChunkLoadError$/,
      '_avast_submit',
      /RenderingCancelledException/,
      /UnknownErrorException.*ensureDoc/,
      /Transport destroyed/,
      /AbortError.*play\(\)/,
      /^LaunchDarklyFlagFetchError/,
      /bufferBelongsToIframe/,
      /ResizeObserver loop/,
      /Non-Error promise rejection captured.*Object Not Found Matching Id/,
    ],
    denyUrls: [EXTENSION_URL_RE],
    beforeSend(event, hint) {
      const error = hint?.originalException as
        | (Error & {
            name?: string;
            message?: string;
            response?: { status?: number };
          })
        | undefined;

      if (error?.name === 'AbortError' || error?.name === 'CanceledError') {
        return null;
      }

      const message = error?.message || event.message || '';
      if (
        /Network Error|Failed to fetch|Load failed|network error/i.test(message)
      ) {
        return null;
      }

      const status = error?.response?.status;
      if (status && status >= 500 && status <= 599) {
        return null;
      }

      const topFrame =
        event.exception?.values?.[0]?.stacktrace?.frames?.slice(-1)[0];
      const topFile = topFrame?.filename || '';
      if (EXTENSION_URL_RE.test(topFile) || topFile.startsWith('blob:')) {
        return null;
      }

      return event;
    },
  });
}

// Before the first render, after `Sentry.init` so recoveries get reported: a
// node rewritten outside React (page translation, an extension) otherwise
// throws mid-commit and takes the whole page down. See `domMutationGuards`.
installDomMutationGuards();

const container = document.getElementById('root');
const root = createRoot(container);

const AppToRender = () => {
  const isAssessment = window?.location?.host?.split('.')?.[0] === 'assessment';
  const subdomain = window?.location?.host?.split('.')?.[0];
  //https://integration.kula.ai/rippling
  const isIntegration = subdomain === 'integration';
  const isCandidateSurvey = subdomain === 'survey';
  const isConsent = subdomain === 'consent';

  if (isAssessment) {
    return <AssessmentApp />;
  }
  if (isIntegration) {
    return <IntegrationApp />;
  }
  if (isCandidateSurvey) {
    // TODO: Uncomment this once the candidate survey app is ready
    return <CandidateSurveyApp />;
  }
  if (isConsent) {
    return <CandidateConsentApp />;
  }
  return <Core />;
};

export const AppWithLD = withLDProvider({
  clientSideID: import.meta.env.VITE_LAUNCHDARKLY,
  context: {
    kind: 'user',
    key: `${window.location.host.split('.')[0]}:anonymous`,
  },
  options: {
    streaming: false,
    // SDK health telemetry posted back to LaunchDarkly about LaunchDarkly — it
    // carries none of our data and nothing reads it on our side. Sentry measures
    // `events/diagnostic` at p75 1,136ms on /tasks/jobs/approvals and 1,448ms on
    // /settings/home, which is a lot of page-load budget for a vendor's own
    // metrics. `sendEvents` is deliberately left alone: that one carries flag
    // evaluations the team may actually be reading.
    diagnosticOptOut: true,
    // Milliseconds, not seconds. The SDK validates this as
    // `{ default: 2e3, minimum: 2e3 }` and hands it straight to
    // `eventsFlushIntervalMillis`, so anything below 2000 is silently clamped
    // back to the default — which is exactly how the first attempt here (`30`)
    // shipped as a no-op. Caught on demo2: `events/bulk` kept going out 2s
    // apart with the option set.
    //
    // At the default, `events/bulk` is the largest single consumer of request
    // time on /jobs/:jobId — 2.73 POSTs per page load, 34,103 over seven days,
    // p50 322ms each, all competing with the page's own requests for
    // connections. 30s sends the same events in far fewer beacons, and the SDK
    // still flushes on page hide, so none are lost.
    flushInterval: 30000,
  },
})(AppToRender);

// posthog config
//
// `import()` rather than a static import: `posthog-js` is 172 KB of the entry
// chunk, and the entry chunk is the only thing Sentry traces show on the LCP
// path for /jobs/:jobId. Deferring `init` alone (the previous version of this)
// moved PostHog's own sub-bundle fetches off the critical path but left the
// module itself being parsed on every load.
//
// Everything else is unchanged: `init` still fires the initial $pageview, just
// after the app has painted. A `capture` made in the first moments is still a
// no-op, so `whenIdle`'s timeout stays short. See `@/lib/posthogClient`.
whenIdle(() => {
  void loadPosthog();
});

export const App = () => {
  if (import.meta.env.VITE_LAUNCHDARKLY) {
    return <AppWithLD />;
  }
  // No PostHogProvider: consumers read the client from `@/lib/posthogClient`
  // instead. The provider only ever wrapped this branch, so with LaunchDarkly
  // configured — which is every deployed environment — `usePostHog()` was
  // already resolving through the SDK's module-scope default rather than
  // through context.
  return <AppToRender />;
};

root.render(<App />);
